import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { SOUND_OPTIONS, playSound } from '../../utils/sounds';
import { API_URL } from '../../utils/api';
import RankBadge from '../elo/RankBadge';
import './Settings.css';
import SupportBox from '../donations/SupportBox';
import ConnectedPlatforms from './ConnectedPlatforms';
import ThemeAttribution from './ThemeAttribution';
import ThemePicker from './ThemePicker';
import DonateTab from './DonateTab';
import PushSettings from './PushSettings';
import CountrySelect from '../leagues/CountrySelect';
import CountryFlag from '../common/CountryFlag';
import './Profile.css';

/**
 * Settings card to set the player's own country flag (shown on their profile and
 * wherever they appear). Persists to the player-country endpoint.
 *
 * @param {object} props - Component props.
 * @param {string} props.steamId - The logged-in player's steamid64.
 * @param {Function} props.getAuthHeaders - Auth header provider.
 */
function CountryCard({ steamId, getAuthHeaders }) {
  const { t } = useTranslation();
  const [country, setCountry] = useState('');
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!steamId) return;
    fetch(`${API_URL}/api/player/${steamId}/country`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setCountry(d.country || ''); setDraft(d.country || ''); } })
      .catch(() => {});
  }, [steamId]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const r = await fetch(`${API_URL}/api/player/${steamId}/country`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ country: draft }),
      });
      if (r.ok) {
        const d = await r.json().catch(() => ({}));
        setCountry(d.country || '');
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!steamId) return null;

  return (
    <div className="settings-card">
      <h2 className="settings-card-title">{t('profilePage.countryTitle')}</h2>
      <p className="settings-card-desc">
        {t('profilePage.countryDesc')}
      </p>
      <div className="settings-row settings-country-row">
        {country && <CountryFlag code={country} size="lg" />}
        <CountrySelect value={draft} onChange={setDraft} />
        <button className="settings-save-btn" onClick={save} disabled={saving || draft === country}>
          {saving ? t('settings.saving') : t('settings.save')}
        </button>
        {saved && <span className="settings-success">{t('settings.saved')}</span>}
      </div>
    </div>
  );
}

const RARITY_COLORS = {
  consumer: '#B0C3D9',
  industrial: '#5E98D9',
  mil_spec: '#4B69FF',
  restricted: '#8847FF',
  classified: '#D32CE6',
  covert: '#EB4B4B',
  contraband: '#E4AE39',
};

const TRADE_STATUS_LABELS = {
  pending: { labelKey: 'profilePage.dropPending', icon: '⏳', color: 'var(--text-muted)' },
  sent: { labelKey: 'profilePage.dropSent', icon: '📤', color: 'var(--accent-blue)' },
  accepted: { labelKey: 'profilePage.dropAccepted', icon: '✅', color: 'var(--accent-green)' },
  declined: { labelKey: 'profilePage.dropDeclined', icon: '❌', color: 'var(--accent-red)' },
  manual: { labelKey: 'profilePage.dropManual', icon: '🤝', color: 'var(--accent-gold)' },
  expired: { labelKey: 'profilePage.dropExpired', icon: '⏰', color: 'var(--accent-red)' },
  failed: { labelKey: 'profilePage.dropFailed', icon: '⚠️', color: 'var(--accent-red)' },
};

function DropsTab({ getAuthHeaders }) {
  const { t } = useTranslation();
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [declining, setDeclining] = useState(null);
  const [declineError, setDeclineError] = useState('');
  const navigate = useNavigate();

  const fetchDrops = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/settings/my-drops`, { headers: getAuthHeaders() });
      if (r.ok) {
        const data = await r.json();
        setDrops(data);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchDrops();
  }, [fetchDrops]);

  const claimable = drops.filter(d =>
    ['pending', 'failed', 'expired', 'declined'].includes(d.trade_status)
  );

  const handleClaim = async () => {
    setClaiming(true);
    setClaimResult(null);
    try {
      const r = await fetch(`${API_URL}/api/settings/claim-drops`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await r.json();
      if (r.ok) {
        setClaimResult({ success: true, results: data.results || [] });
        fetchDrops();
      } else {
        setClaimResult({ success: false, error: data.error || t('profilePage.claimFailed') });
      }
    } catch {
      setClaimResult({ success: false, error: t('settings.networkError') });
    } finally {
      setClaiming(false);
    }
  };

  /**
   * Decline a single drop. PR T: dropet markeras player_declined och
   * skin går tillbaka till poolen. Confirm-dialog för att undvika oavsiktlig
   * nej-tack (action är irreversibel ur spelarens perspektiv — admin kan
   * fortfarande rerolla från admin-panelen om de vill ge tillbaka).
   */
  const handleDecline = async (drop) => {
    const name = drop.prize?.display_name || drop.prize?.name || 'detta drop';
    if (!window.confirm(t('profilePage.declineConfirm', { name }))) return;
    setDeclining(drop.id);
    setDeclineError('');
    try {
      const r = await fetch(`${API_URL}/api/settings/drops/${drop.id}/decline`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        await fetchDrops();
      } else {
        let detail = '';
        try { const d = await r.json(); detail = d.error || d.detail || ''; } catch {}
        setDeclineError(detail || `Kunde inte neka drop (HTTP ${r.status})`);
      }
    } catch {
      setDeclineError(t('settings.networkError'));
    } finally {
      setDeclining(null);
    }
  };

  if (loading) {
    return <div className="profile-loading">{t('profilePage.loadingDrops')}</div>;
  }

  if (drops.length === 0) {
    return (
      <div className="profile-empty">
        <span className="profile-empty-icon">🎁</span>
        <p>{t('profilePage.noDrops')}</p>
        <p className="profile-empty-hint">{t('profilePage.noDropsHint')}</p>
      </div>
    );
  }

  return (
    <div className="drops-tab">
      {claimable.length > 0 && (
        <div className="drops-claim-bar">
          <span>{t('profilePage.claimableCount', { count: claimable.length })}</span>
          <button
            className="drops-claim-btn"
            onClick={handleClaim}
            disabled={claiming}
          >
            {claiming ? t('profilePage.sendingTrade') : t('profilePage.claimAll')}
          </button>
        </div>
      )}

      {claimResult && (
        <div className={`drops-claim-result ${claimResult.success ? 'success' : 'error'}`}>
          {claimResult.success ? (
            <span>{t('profilePage.tradesSent')}</span>
          ) : (
            <span>{claimResult.error}</span>
          )}
        </div>
      )}

      <div className="drops-list">
        {drops.map(drop => {
          const prize = drop.prize || {};
          const rarity = prize.rarity || 'mil_spec';
          const borderColor = RARITY_COLORS[rarity] || '#4B69FF';
          const statusInfo = TRADE_STATUS_LABELS[drop.trade_status] || TRADE_STATUS_LABELS.pending;
          const isClaimable = ['pending', 'failed', 'expired', 'declined'].includes(drop.trade_status);

          return (
            <div
              key={drop.id}
              className={`drop-card${isClaimable ? ' claimable' : ''}`}
              style={{ borderLeftColor: borderColor }}
            >
              {prize.image_url && (
                <img
                  className="drop-card-image"
                  src={prize.image_url}
                  alt={prize.display_name}
                  style={{ filter: `drop-shadow(0 0 6px ${borderColor}60)` }}
                />
              )}
              <div className="drop-card-info">
                <div className="drop-card-name">
                  {prize.stattrak && <span className="drop-card-stattrak">ST </span>}
                  {prize.name}
                </div>
                <div className="drop-card-meta" style={{ color: borderColor }}>
                  {prize.condition_display} · {prize.rarity_display}
                </div>
                <button
                  className="drop-card-match-link"
                  onClick={() => navigate(`/stats/matches/${drop.match}`)}
                >
                  Match #{drop.match}
                </button>
              </div>
              <div className="drop-card-right">
                {prize.estimated_value_sek && (
                  <div className="drop-card-value">~{prize.estimated_value_sek} SEK</div>
                )}
                <div className="drop-card-status" style={{ color: statusInfo.color }}>
                  {statusInfo.icon} {t(statusInfo.labelKey)}
                </div>
                {isClaimable && (
                  <button
                    className="drop-card-decline"
                    onClick={() => handleDecline(drop)}
                    disabled={declining === drop.id}
                    title={t('profilePage.declineTitle')}
                  >
                    {declining === drop.id ? 'Nekar...' : 'Nej tack'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {declineError && (
        <div className="drops-claim-result error">{declineError}</div>
      )}
    </div>
  );
}

function SettingsTab({
  settings, updateSetting, ownedThemes, followsCommunity, communityTheme,
  user, isLoggedIn, getAuthHeaders,
}) {
  const { t, i18n } = useTranslation();
  // Trade URL state
  const [tradeUrl, setTradeUrl] = useState('');
  const [tradeUrlLoading, setTradeUrlLoading] = useState(false);
  const [tradeUrlSaved, setTradeUrlSaved] = useState(false);
  const [tradeUrlError, setTradeUrlError] = useState('');
  const [tradeUrlOriginal, setTradeUrlOriginal] = useState('');

  // Auto-accept state
  const [autoaccept, setAutoaccept] = useState(false);
  const [guildId, setGuildId] = useState(null);

  // Fetch guild config
  useEffect(() => {
    fetch(`${API_URL}/api/web/config`)
      .then(r => r.json())
      .then(data => {
        if (data?.guild?.guild_id) setGuildId(data.guild.guild_id);
      })
      .catch(() => {});
  }, []);

  // Fetch trade URL on mount
  useEffect(() => {
    if (!isLoggedIn) return;
    fetch(`${API_URL}/api/settings/trade-url`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.trade_url) {
          setTradeUrl(data.trade_url);
          setTradeUrlOriginal(data.trade_url);
        }
      })
      .catch(() => {});
  }, [isLoggedIn, getAuthHeaders]);

  // Fetch auto-accept status
  useEffect(() => {
    if (!isLoggedIn || !guildId) return;
    fetch(`${API_URL}/api/queue/${guildId}`)
      .then(r => r.json())
      .then(data => {
        if (user && data.autoaccept?.includes(user.discord_id)) {
          setAutoaccept(true);
        }
      })
      .catch(() => {});
  }, [isLoggedIn, guildId, user]);

  const handleSaveTradeUrl = async () => {
    setTradeUrlLoading(true);
    setTradeUrlError('');
    setTradeUrlSaved(false);
    try {
      const r = await fetch(`${API_URL}/api/settings/trade-url`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ trade_url: tradeUrl }),
      });
      const data = await r.json();
      if (!r.ok) {
        setTradeUrlError(data.error || data.detail || t('settings.couldNotSave'));
      } else {
        setTradeUrlSaved(true);
        setTradeUrlOriginal(tradeUrl);
        setTimeout(() => setTradeUrlSaved(false), 3000);
      }
    } catch {
      setTradeUrlError(t('settings.networkError'));
    } finally {
      setTradeUrlLoading(false);
    }
  };

  const handleAutoaccept = async () => {
    if (!guildId) return;
    try {
      const r = await fetch(`${API_URL}/api/queue/${guildId}/autoaccept`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await r.json();
      setAutoaccept(data.autoaccept);
    } catch {
      console.error('Autoaccept toggle failed');
    }
  };

  const tradeUrlChanged = tradeUrl !== tradeUrlOriginal;

  const notifOptions = [
    { key: 'notify_match_ended', label: t('settings.notifMatchEnded') },
    { key: 'notify_veto_started', label: t('settings.notifVetoStarted') },
    { key: 'notify_match_created', label: t('settings.notifMatchCreated') },
    { key: 'notify_match_launched', label: t('settings.notifMatchLaunched') },
    { key: 'notify_match_cancelled', label: t('settings.notifMatchCancelled') },
  ];

  return (
    <>
      {/* Språk — personligt val (localStorage via i18next), Johans beslut:
          väljaren bor här i kugghjulets inställningsflik, inte i headern. */}
      <div className="settings-card">
        <h2 className="settings-card-title">{t('language.label')}</h2>
        <p className="settings-card-desc">{t('settings.languageDesc')}</p>
        <div className="settings-row">
          <label className="settings-label">{t('language.label')}</label>
          <select
            className="settings-lang-select"
            value={i18n.resolvedLanguage}
            onChange={e => i18n.changeLanguage(e.target.value)}
          >
            {SUPPORTED_LANGUAGES.map(l => (
              <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Country / flag */}
      <CountryCard steamId={user?.steam_id} getAuthHeaders={getAuthHeaders} />

      {/* Sound Settings */}
      <div className="settings-card">
        <h2 className="settings-card-title">{t('settings.soundTitle')}</h2>
        <p className="settings-card-desc">{t('settings.soundDesc')}</p>

        <div className="settings-row">
          <label className="settings-label">{t('settings.sound')}</label>
          <div className="settings-sound-select">
            <select
              value={settings.matchFoundSound}
              onChange={e => updateSetting('matchFoundSound', e.target.value)}
            >
              {SOUND_OPTIONS.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {settings.matchFoundSound !== 'none' && (
              <button
                className="settings-test-btn"
                onClick={() => playSound(settings.matchFoundSound, settings.soundVolume)}
              >
                {t('settings.test')}
              </button>
            )}
          </div>
        </div>

        <div className="settings-row">
          <label className="settings-label">{t('settings.volume')}</label>
          <div className="settings-volume">
            <input
              type="range"
              min="0"
              max="100"
              value={settings.soundVolume}
              onChange={e => updateSetting('soundVolume', parseInt(e.target.value))}
              disabled={settings.matchFoundSound === 'none'}
            />
            <span className="settings-volume-val">{settings.soundVolume}%</span>
          </div>
        </div>
      </div>

      {/* Trade URL */}
      <div className="settings-card">
        <h2 className="settings-card-title">Steam Trade URL</h2>
        <p className="settings-card-desc">
          {t('settings.tradeDesc')}{' '}
          <a href="https://steamcommunity.com/my/tradeoffers/privacy#trade_offer_access_url" target="_blank" rel="noopener noreferrer">
            Steam Trade Offers
          </a>
        </p>

        <div className="settings-row">
          <input
            type="text"
            className="settings-input"
            placeholder="https://steamcommunity.com/tradeoffer/new/?partner=...&token=..."
            value={tradeUrl}
            onChange={e => { setTradeUrl(e.target.value); setTradeUrlError(''); setTradeUrlSaved(false); }}
          />
        </div>
        <div className="settings-row settings-trade-actions">
          <button
            className="settings-save-btn"
            onClick={handleSaveTradeUrl}
            disabled={tradeUrlLoading || !tradeUrlChanged}
          >
            {tradeUrlLoading ? t('settings.saving') : t('settings.save')}
          </button>
          {tradeUrlSaved && <span className="settings-success">{t('settings.saved')}</span>}
          {tradeUrlError && <span className="settings-error">{tradeUrlError}</span>}
        </div>
      </div>

      {/* Notifications */}
      <div className="settings-card">
        <h2 className="settings-card-title">{t('settings.notifTitle')}</h2>
        <p className="settings-card-desc">{t('settings.notifDesc')}</p>

        {notifOptions.map(opt => (
          <div key={opt.key} className="settings-row settings-toggle-row">
            <label className="settings-label">{opt.label}</label>
            <label className="settings-toggle">
              <input
                type="checkbox"
                checked={settings[opt.key] !== false}
                onChange={e => updateSetting(opt.key, e.target.checked)}
              />
              <span className="settings-toggle-slider" />
            </label>
          </div>
        ))}
      </div>

      {/* Push till enheten. Egen komponent + serverside-inställningar: valen
          ovan ligger i localStorage och filtrerar bara toasts i den öppna
          fliken, medan push avgörs på servern. */}
      <PushSettings />

      {/* Queue */}
      <div className="settings-card">
        <h2 className="settings-card-title">{t('settings.queueTitle')}</h2>

        <div className="settings-row settings-toggle-row">
          <label className="settings-label">{t('settings.autoAccept')}</label>
          <label className="settings-toggle">
            <input
              type="checkbox"
              checked={autoaccept}
              onChange={handleAutoaccept}
            />
            <span className="settings-toggle-slider" />
          </label>
        </div>
      </div>

      {/* Theme */}
      <div className="settings-card">
        <h2 className="settings-card-title">{t('settings.appearanceTitle')}</h2>

        <ThemePicker
          value={settings.theme}
          owned={ownedThemes}
          followsCommunity={followsCommunity}
          communityTheme={communityTheme}
          onChange={themeId => updateSetting('theme', themeId)}
        />

        {/* Licensvillkor, inte artighet: Blizzards riktlinjer kräver att
            upphovsrättsnotiserna behålls där materialet visas. */}
        <ThemeAttribution />
      </div>
    </>
  );
}

function QuickStats({ steamId }) {
  const { t } = useTranslation();
  const [elo, setElo] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!steamId) { setLoading(false); return; }
    let mounted = true;

    Promise.all([
      fetch(`${API_URL}/api/elo/player/${steamId}`).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/player/${steamId}`).then(r => r.ok ? r.json() : null),
    ]).then(([eloData, playerData]) => {
      if (!mounted) return;
      if (eloData && !eloData.error) setElo(eloData);
      if (playerData?.recent_matches) {
        setRecentMatches(playerData.recent_matches.slice(0, 5));
      }
      setLoading(false);
    }).catch(() => { if (mounted) setLoading(false); });

    return () => { mounted = false; };
  }, [steamId]);

  if (loading || !steamId) return null;

  const matchCount = elo?.matches_played || 0;
  const winRate = elo?.win_rate != null ? Math.round(elo.win_rate) : null;
  const lastChange = elo?.recent_changes?.length > 0
    ? elo.recent_changes[elo.recent_changes.length - 1].elo_change
    : null;

  return (
    <div className="quick-stats">
      {/* ELO card */}
      {elo && (
        <div className="quick-stats-row">
          <div className="quick-stat">
            <RankBadge elo={elo.elo} rank={elo.rank} size="medium" />
            <span className="quick-stat-value">{elo.elo}</span>
            <span className="quick-stat-label">ELO</span>
          </div>
          <div className="quick-stat">
            <span className="quick-stat-value">{matchCount}</span>
            <span className="quick-stat-label">{t('players.colMatches')}</span>
          </div>
          {winRate != null && (
            <div className="quick-stat">
              <span className={`quick-stat-value ${winRate >= 50 ? 'positive' : 'negative'}`}>{winRate}%</span>
              <span className="quick-stat-label">{t('profilePage.wins')}</span>
            </div>
          )}
          {lastChange != null && lastChange !== 0 && (
            <div className="quick-stat">
              <span className={`quick-stat-value ${lastChange > 0 ? 'positive' : 'negative'}`}>
                {lastChange > 0 ? '+' : ''}{lastChange}
              </span>
              <span className="quick-stat-label">{t('profilePage.lastElo')}</span>
            </div>
          )}
        </div>
      )}

      {/* Recent matches */}
      {recentMatches.length > 0 && (
        <div className="recent-matches-mini">
          <h3 className="recent-matches-mini-title">{t('profile.recentMatches')}</h3>
          {recentMatches.map(m => {
            const isTeam1 = m.team === m.team1_name;
            const myScore = isTeam1 ? m.team1_score : m.team2_score;
            const opScore = isTeam1 ? m.team2_score : m.team1_score;
            const won = myScore > opScore;
            const mapName = m.mapname?.replace('de_', '') || m.map_tag || '?';

            return (
              <button
                key={m.matchid}
                className={`mini-match-row ${won ? 'win' : 'loss'}`}
                onClick={() => navigate(`/stats/matches/${m.matchid}`)}
              >
                <span className={`mini-match-result ${won ? 'win' : 'loss'}`}>
                  {won ? 'W' : 'L'}
                </span>
                <span className="mini-match-score">{myScore}–{opScore}</span>
                <span className="mini-match-map">{mapName}</span>
                <span className="mini-match-kda">{m.kills}/{m.deaths}/{m.assists}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * User profile page with tabs for drops, settings, and community support.
 * Requires authentication to view.
 */
export default function Profile() {
  const { t } = useTranslation();
  const {
    settings, updateSetting, ownedThemes, followsCommunity, communityTheme,
  } = useSettings();
  const { user, isLoggedIn, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('drops');

  if (!isLoggedIn) {
    return (
      <div className="profile-page">
        <h1 className="profile-title">{t('profilePage.title')}</h1>
        <p className="profile-login-msg">{t('profilePage.loginMsg')}</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-settings-header">
        <div className="profile-user">
          {user.steam_avatar && (
            <img src={user.steam_avatar} alt="" className="profile-avatar" />
          )}
          <div className="profile-user-info">
            <h1 className="profile-settings-name">{user.steam_username || user.discord_username || user.username}</h1>
            {user.steam_id && (
              <button
                className="profile-stats-link"
                onClick={() => navigate(`/stats/players/${user.steam_id}`)}
              >
                Visa min statistik
              </button>
            )}
          </div>
        </div>
      </div>

      {user.steam_id && <QuickStats steamId={user.steam_id} />}

      <div className="profile-settings-tabs">
        <button
          className={`profile-settings-tab${activeTab === 'drops' ? ' active' : ''}`}
          onClick={() => setActiveTab('drops')}
        >
          {t('profilePage.tabDrops')}
        </button>
        <button
          className={`profile-settings-tab${activeTab === 'settings' ? ' active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          {t('settings.title')}
        </button>
        <button
          className={`profile-settings-tab${activeTab === 'voice' ? ' active' : ''}`}
          onClick={() => setActiveTab('voice')}
        >
          Voice
        </button>
        <button
          className={`profile-settings-tab${activeTab === 'donate' ? ' active' : ''}`}
          onClick={() => setActiveTab('donate')}
        >
          {t('profilePage.tabDonate')}
        </button>
        <button
          className={`profile-settings-tab${activeTab === 'support' ? ' active' : ''}`}
          onClick={() => setActiveTab('support')}
        >
          {t('profilePage.tabSupport')}
        </button>
      </div>

      <div className="profile-content">
        {activeTab === 'drops' && (
          <DropsTab getAuthHeaders={getAuthHeaders} />
        )}
        {activeTab === 'settings' && (
          <SettingsTab
            settings={settings}
            updateSetting={updateSetting}
            ownedThemes={ownedThemes}
            user={user}
            isLoggedIn={isLoggedIn}
            getAuthHeaders={getAuthHeaders}
          />
        )}
        {activeTab === 'voice' && (
          <ConnectedPlatforms getAuthHeaders={getAuthHeaders} user={user} />
        )}
        {activeTab === 'donate' && (
          <DonateTab />
        )}
        {activeTab === 'support' && (
          <SupportBox />
        )}
      </div>
    </div>
  );
}
